import React, { useEffect, useRef, useState } from 'react'
import { Dialog, DialogHeader } from '@astryxdesign/core/Dialog'
import { Avatar } from '@astryxdesign/core/Avatar'
import { DEFAULT_AVATAR_IMAGE } from '../lib/defaultAvatarImage'
import { Grid } from '@astryxdesign/core/Grid'
import { VStack } from '@astryxdesign/core/VStack'
import { Text } from '@astryxdesign/core/Text'
import { Spinner } from '@astryxdesign/core/Spinner'
import { Banner } from '@astryxdesign/core/Banner'
import { SegmentedControl, SegmentedControlItem } from '@astryxdesign/core/SegmentedControl'
import { cn } from '@renderer/lib/utils'

/** Filter order is fixed so the control does not reshuffle as the library changes. */
/** Tile size in the picker. Astryx's largest named size (128px) reads as a hero portrait,
 * which is too much for a grid of twenty-odd options. */
const PICKER_AVATAR_SIZE = 72

const GENDER_FILTERS: { value: DefaultAvatar['gender']; label: string }[] = [
  { value: 'woman', label: 'Women' },
  { value: 'man', label: 'Men' },
  // The stored value stays 'nonbinary' (it is in the default_avatars CHECK constraint); only the
  // label users read is 'Other'.
  { value: 'nonbinary', label: 'Other' }
]

interface DefaultAvatarPickerProps {
  isOpen: boolean
  currentAvatarUrl: string | null
  onSelect: (defaultAvatarId: string) => Promise<void>
  onClose: () => void
}

export default function DefaultAvatarPicker({
  isOpen,
  currentAvatarUrl,
  onSelect,
  onClose
}: DefaultAvatarPickerProps): React.JSX.Element | null {
  const [avatars, setAvatars] = useState<DefaultAvatar[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | DefaultAvatar['gender']>('all')
  const [busyId, setBusyId] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDialogElement | null>(null)

  // Reloaded per open so an avatar the admin added mid-session shows up without a refresh.
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    setAvatars(null)
    setError(null)
    setFilter('all')
    // Routed through an extra promise tick, matching the probe in SettingsPanel.tsx, so a
    // rejection (including `window.api` not being available at all — e.g. a test that
    // renders this picker directly without the app's api-bridge loaded) always lands in
    // `.catch` rather than throwing synchronously out of the effect.
    void Promise.resolve()
      .then(() => window.api.profiles.listDefaultAvatars())
      .then((list) => {
        if (!cancelled) setAvatars(list)
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.message || 'Could not load avatars.')
      })
    return () => {
      cancelled = true
    }
  }, [isOpen])

  /**
   * This dialog is nested inside the settings Dialog (a native <dialog> inside another
   * native <dialog>). Astryx's Dialog dismisses on Escape via a `keydown` listener it
   * attaches directly to its own <dialog> node; that handler calls `preventDefault()`
   * (which suppresses the browser's native dialog-cancel behavior) but not
   * `stopPropagation()`. Because this picker's <dialog> renders as a DOM descendant of
   * the settings dialog, the same keydown keeps bubbling after the picker closes and
   * reaches the settings dialog's own listener, which treats it as its own Escape too —
   * confirmed by testing: without this, one Escape press closed both layers at once.
   * Stopping propagation here is the same fix already applied to the file input's
   * `cancel` event in SettingsPanel.tsx, just intercepting the mechanism that actually
   * drives Dialog's Escape handling (`keydown`) rather than the native `cancel` event,
   * which never fires here because `preventDefault()` on the keydown suppresses it.
   */
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog || !isOpen) return
    const stopEscapePropagation = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') event.stopPropagation()
    }
    dialog.addEventListener('keydown', stopEscapePropagation)
    return () => dialog.removeEventListener('keydown', stopEscapePropagation)
  }, [isOpen])

  if (!isOpen) return null

  // Only genders actually represented in the library get a filter segment.
  const availableFilters = GENDER_FILTERS.filter((option) =>
    (avatars || []).some((avatar) => avatar.gender === option.value)
  )
  const visible = (avatars || []).filter((avatar) => filter === 'all' || avatar.gender === filter)

  const handlePick = async (avatar: DefaultAvatar): Promise<void> => {
    setBusyId(avatar.id)
    setError(null)
    try {
      await onSelect(avatar.id)
      onClose()
    } catch (err: any) {
      setError(err?.message || 'Could not set that avatar.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <Dialog
      ref={dialogRef}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      // Four 136px cells plus three 12px gaps need 580px of content; the Dialog's own
      // --spacing-4 padding on each side adds 32. 560 was too narrow, which is what made the
      // grid compute a fifth track and overlap the avatars.
      width={640}
      purpose="form"
    >
      <VStack gap={5}>
        <DialogHeader
          title="Choose an avatar"
          onOpenChange={(open) => {
            if (!open) onClose()
          }}
        />

        {error && <Banner status="error" title="Something went wrong" description={error} />}

        {avatars === null ? (
          // A failed fetch leaves `avatars` null with `error` set — that means "we don't know
          // whether the library is empty", not "it's empty", so nothing renders here beyond
          // the error Banner above. Only show the spinner while a fetch is genuinely in flight.
          !error && <Spinner size="lg" label="Loading avatars" />
        ) : (
          <VStack gap={4}>
            {availableFilters.length > 1 && (
              <SegmentedControl
                label="Filter avatars by gender"
                value={filter}
                onChange={(value) => setFilter(value as 'all' | DefaultAvatar['gender'])}
                layout="fill"
                size="sm"
              >
                <SegmentedControlItem value="all" label="All" />
                {availableFilters.map((option) => (
                  <SegmentedControlItem
                    key={option.value}
                    value={option.value}
                    label={option.label}
                  />
                ))}
              </SegmentedControl>
            )}

            {visible.length === 0 ? (
              <Text as="p" type="supporting">
                No avatars are available yet.
              </Text>
            ) : (
              // minWidth must be at least the rendered cell, or the grid computes more tracks
              // than the avatars can fit in and they overlap: each Avatar is PICKER_AVATAR_SIZE
              // and the button adds --spacing-1 (4px) either side. `max: 6` keeps rows readable;
              // `fill` (not `fit`) preserves empty tracks so cells stay a consistent width and
              // stay left-aligned when only one or two avatars match a filter.
              <section className="default-avatar-grid-scroll">
                <Grid
                  columns={{ minWidth: PICKER_AVATAR_SIZE + 8, max: 6, repeat: 'fill' }}
                  gap={3}
                >
                  {visible.map((avatar) => {
                    const isCurrent =
                      Boolean(avatar.avatarUrl) && avatar.avatarUrl === currentAvatarUrl
                    return (
                      <button
                        key={avatar.id}
                        type="button"
                        className={cn(
                          'default-avatar-option',
                          isCurrent && 'default-avatar-option-selected'
                        )}
                        onClick={() => void handlePick(avatar)}
                        disabled={busyId !== null}
                        aria-pressed={isCurrent}
                        aria-label={avatar.name}
                        title={avatar.name}
                      >
                        <Avatar
                          size={PICKER_AVATAR_SIZE}
                          src={avatar.avatarUrl ?? DEFAULT_AVATAR_IMAGE}
                          name={avatar.name}
                        />
                      </button>
                    )
                  })}
                </Grid>
              </section>
            )}
          </VStack>
        )}
      </VStack>
    </Dialog>
  )
}
